import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Paper,
  Stack,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  Science as TestIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { useIsAdmin } from '../../../contexts/PermissionsContext';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

const isHtmlEmpty = (html) => {
  if (!html) return true;
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const text = (parsed.body?.textContent || '').trim();
  return text.length === 0;
};

const formatScheduledDateTime = (dateTimeValue) => {
  if (!dateTimeValue) return '';
  const date = new Date(dateTimeValue);
  if (Number.isNaN(date.getTime())) return dateTimeValue.replace('T', ' ');

  const formattedDate = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);

  const formattedTime = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date).toLowerCase();

  return `${formattedDate}, ${formattedTime}`;
};

const ComposeEmailPage = () => {
  const isAdmin = useIsAdmin();

  // Form state
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [scope, setScope] = useState('organization');
  const [selectedLeagueIds, setSelectedLeagueIds] = useState([]);
  const [testEmail, setTestEmail] = useState('');
  const [scheduleSend, setScheduleSend] = useState(false);
  const [scheduledSendDateTime, setScheduledSendDateTime] = useState('');

  // Data state
  const [leagues, setLeagues] = useState([]);
  const [leaguesLoading, setLeaguesLoading] = useState(false);

  // Attachment state
  const [attachments, setAttachments] = useState([]); // { url, fileName, fileSizeBytes }
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Send state
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Test state
  const [testSending, setTestSending] = useState(false);
  const [testError, setTestError] = useState(null);
  const [testSuccess, setTestSuccess] = useState(null);

  const fetchLeagues = useCallback(async () => {
    setLeaguesLoading(true);
    try {
      const data = await unifiedApi.getLeagues();
      setLeagues(data || []);
    } catch {
      // silently fail — leagues list is optional
    } finally {
      setLeaguesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeagues();
  }, [fetchLeagues]);

  const handleLeagueToggle = (leagueId) => {
    setSelectedLeagueIds((prev) =>
      prev.includes(leagueId) ? prev.filter((id) => id !== leagueId) : [...prev, leagueId]
    );
  };

  const handleAttachFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploadLoading(true);
    try {
      const result = await unifiedApi.uploadEmailAttachment(file);
      setAttachments((prev) => [...prev, result]);
    } catch (err) {
      setUploadError(err.message || 'Failed to upload attachment.');
    } finally {
      setUploadLoading(false);
      e.target.value = '';
    }
  };

  const handleRemoveAttachment = async (url) => {
    try {
      await unifiedApi.deleteEmailAttachment(url);
    } catch {
      // best-effort delete
    }
    setAttachments((prev) => prev.filter((a) => a.url !== url));
  };

  const validateForm = () => {
    if (!subject.trim()) return 'Subject is required.';
    if (isHtmlEmpty(body)) return 'Body is required.';
    if (scope === 'leagues' && selectedLeagueIds.length === 0)
      return 'Select at least one league.';
    if (scheduleSend && !scheduledSendDateTime)
      return 'Scheduled date and time are required.';
    return null;
  };

  const buildRequest = () => ({
    subject,
    htmlBody: body,
    scope,
    leagueIds: scope === 'leagues' ? selectedLeagueIds : [],
    attachmentUrls: attachments.map((a) => a.url),
    scheduledSendDateTime: scheduleSend ? scheduledSendDateTime : null,
  });

  const handleSendTest = async () => {
    const err = validateForm();
    if (err) { setTestError(err); return; }
    if (!testEmail.trim()) { setTestError('Enter a test email address.'); return; }

    setTestError(null);
    setTestSuccess(null);
    setTestSending(true);
    try {
      await unifiedApi.sendTestEmail({ ...buildRequest(), testEmail });
      setTestSuccess(`Test email sent to ${testEmail}.`);
    } catch (ex) {
      setTestError(ex.message || 'Failed to send test email.');
    } finally {
      setTestSending(false);
    }
  };

  const handleSendConfirm = async () => {
    setConfirmOpen(false);
    setSendError(null);
    setSendResult(null);
    setSending(true);
    try {
      const result = await unifiedApi.sendBulkEmail(buildRequest());
      setSendResult(result);
    } catch (ex) {
      setSendError(ex.message || 'Failed to send email.');
    } finally {
      setSending(false);
    }
  };

  const handleSend = () => {
    const err = validateForm();
    if (err) { setSendError(err); return; }
    setSendError(null);
    setConfirmOpen(true);
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  if (!isAdmin) {
    return (
      <AdminLayout>
        <Typography variant="h6" color="error">Access Denied</Typography>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, sm: 3 } }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={3}>
          <EmailIcon color="primary" />
          <Typography variant="h4">Compose Email</Typography>
        </Stack>

        {sendResult && (
          <Alert
            severity="success"
            icon={<SuccessIcon />}
            sx={{ mb: 3 }}
            onClose={() => setSendResult(null)}
          >
            {sendResult.message}
          </Alert>
        )}
        {sendError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSendError(null)}>
            {sendError}
          </Alert>
        )}

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Message</Typography>

          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            required
            sx={{ mb: 2 }}
          />

          <Box sx={{
            '& .ql-container': { minHeight: 200, fontSize: '1rem' },
            '& .ql-editor': { minHeight: 200 },
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Body
            </Typography>
            <ReactQuill
              theme="snow"
              value={body}
              onChange={setBody}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Write your message..."
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Rich text formatting is supported. The message will be wrapped in the standard email template.
            </Typography>
          </Box>
        </Paper>

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Audience</Typography>

          <FormControl component="fieldset">
            <FormLabel component="legend">Send to</FormLabel>
            <RadioGroup
              value={scope}
              onChange={(e) => { setScope(e.target.value); setSelectedLeagueIds([]); }}
            >
              <FormControlLabel
                value="organization"
                control={<Radio />}
                label="Entire organization (all members)"
              />
              <FormControlLabel
                value="leagues"
                control={<Radio />}
                label="Selected league(s)"
              />
            </RadioGroup>
          </FormControl>

          {scope === 'leagues' && (
            <Box mt={2}>
              {leaguesLoading ? (
                <CircularProgress size={20} />
              ) : leagues.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No leagues found.</Typography>
              ) : (
                <FormGroup>
                  {leagues.map((league) => (
                    <FormControlLabel
                      key={league.id}
                      control={
                        <Checkbox
                          checked={selectedLeagueIds.includes(league.id)}
                          onChange={() => handleLeagueToggle(league.id)}
                        />
                      }
                      label={league.name}
                    />
                  ))}
                </FormGroup>
              )}
              {selectedLeagueIds.length > 0 && (
                <Box mt={1}>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {leagues
                      .filter((l) => selectedLeagueIds.includes(l.id))
                      .map((l) => (
                        <Chip
                          key={l.id}
                          label={l.name}
                          size="small"
                          onDelete={() => handleLeagueToggle(l.id)}
                        />
                      ))}
                  </Stack>
                </Box>
              )}
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Schedule</Typography>
          <FormControlLabel
            control={(
              <Switch
                checked={scheduleSend}
                onChange={(e) => setScheduleSend(e.target.checked)}
              />
            )}
            label="Schedule for later"
          />
          {scheduleSend && (
            <TextField
              label="Send date and time"
              type="datetime-local"
              value={scheduledSendDateTime}
              onChange={(e) => setScheduledSendDateTime(e.target.value)}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 1 }}
              helperText="Interpreted in your organization's timezone."
            />
          )}
        </Paper>

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Attachments</Typography>

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
              {uploadError}
            </Alert>
          )}

          <List dense disablePadding>
            {attachments.map((a) => (
              <ListItem key={a.url} disableGutters>
                <ListItemText
                  primary={a.fileName}
                  secondary={formatBytes(a.fileSizeBytes)}
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" size="small" onClick={() => handleRemoveAttachment(a.url)} aria-label="Remove attachment">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          <Button
            component="label"
            variant="outlined"
            startIcon={uploadLoading ? <CircularProgress size={16} /> : <AttachFileIcon />}
            disabled={uploadLoading}
            sx={{ mt: attachments.length > 0 ? 1 : 0 }}
          >
            Attach File
            <input type="file" hidden onChange={handleAttachFile} />
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            Max 10 MB per file
          </Typography>
        </Paper>

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Send Test Email</Typography>

          {testSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setTestSuccess(null)}>
              {testSuccess}
            </Alert>
          )}
          {testError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTestError(null)}>
              {testError}
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <TextField
              label="Test email address"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              type="email"
              fullWidth
              size="small"
              sx={{ flex: 1 }}
              placeholder="you@example.com"
            />
            <Button
              variant="outlined"
              startIcon={testSending ? <CircularProgress size={16} /> : <TestIcon />}
              disabled={testSending}
              onClick={handleSendTest}
            >
              Send Test
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Sends the email with a [TEST] prefix to your chosen address. Attachments are included.
          </Typography>
        </Paper>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
            disabled={sending}
            onClick={handleSend}
          >
            Send to {scope === 'organization' ? 'All Members' : `${selectedLeagueIds.length} League(s)`}
          </Button>
        </Box>
      </Box>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Send</DialogTitle>
        <DialogContent>
          <Typography>
            {scheduleSend ? 'Schedule' : 'Send'} <strong>"{subject}"</strong> to{' '}
            {scope === 'organization'
              ? 'all organization members'
              : `members of ${selectedLeagueIds.length} selected league(s)`}?
          </Typography>
          {scheduleSend && scheduledSendDateTime && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              Scheduled for {formatScheduledDateTime(scheduledSendDateTime)} (organization timezone).
            </Typography>
          )}
          {attachments.length > 0 && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              {attachments.length} attachment(s) will be included.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSendConfirm}>
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default ComposeEmailPage;
